/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */

/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import isUrl from "is-url";
import logo from "@content/assets/II_White_On_Black.png";
import { useLocation } from '@reach/router';
import {
  DATA_LAYER_TYPE_ACCESS_TYPE__PUB,
  DATA_LAYER_TYPE_PAYWALL__NONE,
  useDataLayer,
} from '@emarketer/datalayer';
import { useUser } from '@emarketer/auth';

import SeoDetails, { Author, MetaEntry, ParselyTracking } from "@/types/SEO";
import {emConfig as  config } from "@/emConfiguration";

import { trimTrailingSlash } from '@emarketer/code-helpers';
import { isProduction as checkIsProduction, Environment } from '@emarketer/code-helpers';
import Schema from "./Schema";


export interface DataLayerDetails {
  unifiedAssetId: string;
  author?: string;
  section?: string;
  publicationDate?: string;
  modifiedDate?: string
  topics?: string;
  wordCount?: number
  vertical?: string;
}

export const Seo = ({ seo, lang = 'en', title = '', uri = '', author, description = '', parselyTrackingData, isFrontPage = false, 
  skipParsingSchema = false, dataLayerDetails }: {
  seo?: SeoDetails,
  author?: Author,
  lang?: string,
  title?: string,
  uri?: string,
  description?: string,
  parselyTrackingData?: ParselyTracking,
  isFrontPage?: boolean,
  // Set this to true if it's a page generated in here that doesn't use the Yoast json-ld
  skipParsingSchema?: boolean,
  dataLayerDetails?: DataLayerDetails

}): JSX.Element => {
  let meta: MetaEntry[] = [];
  const { wp, site } = useStaticQuery(
    graphql`
      query SeoMetadata {
        wp {
          generalSettings {
            title
            description
          }
          seo {
            social {
              facebook {
                url # og:url ?
                defaultImage {
                  sourceUrl # og:image?
                  mediaDetails {
                    width # og:image:widht?
                    height # og.image:height ?
                  }
                  mediaType # og:image:type ?
                  description
                  title
                }
              }
              twitter {
                cardType # twitter:card = summary
              }
            }
            webmaster {
              googleVerify # google-site-verification
            }
          }
        }
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `
  );
  const { user } = useUser();
  const siteUrl = site?.siteMetadata?.siteUrl;
  const location = useLocation();
  const rawSchema = seo?.schema;
  const seoTitle = seo?.title || title;
  const metaDescription = seo?.metaDesc || description || wp.generalSettings?.description;
  // Use the sites URL, when there is no URI specified (Non Post, Page pages)
  const seoUrl = seo?.uri || uri ? `${siteUrl}${uri}` : location.href;
  const seoImage = seo?.seoImage || logo;

  const defaultogTitle = wp?.seo?.social?.facebook?.title || seoTitle;
  const defaultogDescription =
    wp?.seo?.social?.facebook?.description || metaDescription;
  const defaultogImage =
    wp?.seo?.social?.facebook.defaultImage?.sourceUrl || seoImage;
  const defaultogImageHeight =
    wp?.seo?.social?.facebook?.defaultImage?.mediaDetails?.height;
  const defaultogImageWidth =
    wp?.seo?.social?.facebook?.defaultImage?.mediaDetails?.width;
  const defaultogType = `website`;

  const ogTitle = seo?.opengraphTitle || defaultogTitle;
  const ogDescription = seo?.opengraphDescription || defaultogDescription;
  let ogImage = seo?.opengraphImage?.sourceUrl || defaultogImage;
  const ogImageHeight =
    seo?.opengraphImage?.mediaDetails?.height || defaultogImageHeight;
  const ogImageWidth =
    seo?.opengraphImage?.mediaDetails?.width || defaultogImageWidth;
  const ogSiteName = seo?.opengraphSiteName || "EMARKETER";
  const ogType = seo?.opengraphType || defaultogType;
  let ogURL = seo?.opengraphUrl || seoUrl;

  // From wordpress - Twitter uses Open Graph metadata just like Facebook, 
  // Be sure to keep the "Add Open Graph meta data" setting on the Facebook
  // tab enabled if you want to optimize your site for Twitter.
  const twitterCard = wp?.seo?.social?.twitter.cardType || 'summary';
  const twitterTitle = seo?.twitterTitle || ogTitle;
  const twitterDescription = seo?.twitterDescription || ogDescription;
  let twitterImage = seo?.twitterImage?.sourceUrl || ogImage;

  const googleVerification = wp?.seo?.webmaster?.googleVerify;
  const metaauthor = author?.node?.name;
  const articlePublisher = `https://www.facebook.com/InsiderIntelligence/`;
  let canonical = seo?.canonical;

  if (author) {
    meta.push({ name: `author`, content: metaauthor });
  }

  if (ogImageHeight) {
    meta.push({ property: `og:image:height`, content: ogImageHeight });
  }

  if (ogImageWidth) {
    meta.push({ name: `og:image:width`, content: ogImageWidth });
  }

  // On Gatsby canonical URL doesn't come in as fully qualified URL in contrast to what we can see in WP GraphiQL
  // If user hasn't supplied a fully qualify URL, append the public sites URL to make it fully qualified
  // Note: if a canonical URL is not set in wordpress, canonical is defaulted to the slug of the page
  const appendSiteUrl = () => {
    if (canonical && !isUrl(canonical)) {
      canonical = `${siteUrl}${canonical}`;
    }
    else if (!canonical) {
      canonical = seoUrl;
    }

    if (isFrontPage) {
      canonical = trimTrailingSlash(canonical);
    }


    if (ogURL && !isUrl(ogURL)) {
      ogURL = `${siteUrl}${ogURL}`;
    }

    if (ogImage && !isUrl(ogImage)) {
      ogImage = `${siteUrl}${ogImage}`;
    }

    if (twitterImage && !isUrl(twitterImage)) {
      twitterImage = `${siteUrl}${twitterImage}`;
    }
  };

  // Pull settings for robot from Yoast only on production
  // if not set on production set to follow, index
  const isProduction = checkIsProduction(process.env.GATSBY_ENVIRONMENT as Environment);
  const metaRobotsNofollow = isProduction ? (seo?.metaRobotsNofollow || 'follow') : 'nofollow';
  const metaRobotsNoindex = isProduction ? (seo?.metaRobotsNoindex || 'index') : 'noindex';
  const metaRobotsMaxImagePreview = isProduction ? (seo?.metaRobotsMaxImagePreview || 'max-image-preview:large') : '';

  appendSiteUrl();
  meta = meta.concat([
    {
      name: `description`,
      content: metaDescription,
    },
    {
      name: `google-site-verification`,
      content: googleVerification,
    },
    {
      property: `og:title`,
      content: ogTitle,
    },
    {
      property: `og:description`,
      content: ogDescription,
    },
    {
      property: `og:url`,
      content: ogURL,
    },
    {
      property: `og:type`,
      content: ogType,
    },
    {
      property: `og:site_name`,
      content: ogSiteName,
    },
    {
      property: `og:image`,
      content: ogImage,
    },
    {
      name: `twitter:card`,
      content: twitterCard,
    },
    {
      property: `article:publisher`,
      content: articlePublisher,
    },
    {
      property: `robots`,
      content: `${metaRobotsNoindex}, ${metaRobotsNofollow}, ${metaRobotsMaxImagePreview}`,
    },
  ]);

  // For cleaner output, let's only add the twitter card items that differ from open graph.
  if (twitterDescription !== ogDescription) {
    meta.push({
      name: 'twitter:description',
      content: twitterDescription
    });
  }
  if (twitterImage !== ogImage) {
    meta.push({
      name: 'twitter:image',
      content: twitterImage
    });
  }

  if (twitterTitle !== ogTitle) {
    meta.push({
      name: 'twitter:title',
      content: twitterTitle
    });
  }

  // Statement below allows parsely tool to track user sessions
  if (parselyTrackingData?.useTracking && seo) {
    meta = meta.concat([
      {
        name: 'parsely-title',
        content: seoTitle
      },
      {
        name: 'parsely-link',
        content: canonical
      },
      {
        name: 'parsely-type',
        content: 'post'
      },
      {
        name: 'parsely-image-url',
        content: seo.opengraphImage?.sourceUrl
      },
      {
        name: 'parsely-pub-date',
        content: parselyTrackingData.date
      },
      {
        name: 'parsely-section',
        content: parselyTrackingData.section
      },
      {
        name: 'parsely-author',
        content: parselyTrackingData.author
      },
      {
        name: 'parsely-tags',
        content: parselyTrackingData.tags
      }
    ]);
  }
  
  // data layer hook to add to push data layer props to window.datalayer client-side
  useDataLayer({
    accessType: DATA_LAYER_TYPE_ACCESS_TYPE__PUB,
    isAdBlock: false,
    isLocked: false,
    datePublishedUtc: dataLayerDetails?.publicationDate,
    dateModifiedUtc: dataLayerDetails?.modifiedDate,
    author: dataLayerDetails?.author ?? metaauthor,
    paywallType: DATA_LAYER_TYPE_PAYWALL__NONE,
    unifiedAssetId: dataLayerDetails?.unifiedAssetId,
    vertical: dataLayerDetails?.vertical,
    topics: dataLayerDetails?.topics,
    section: dataLayerDetails?.section,
    userSubscriptionTier: user.subscriptionTier,
    wordCount: dataLayerDetails?.wordCount
  });
  
  return (
    <>
      <html lang={lang} />
      <title>{seoTitle}</title>
      {meta.map((metaItem) => (
        <meta key={metaItem.name || metaItem.property} {...metaItem} />
      ))}
      <link
        rel="preload stylesheet prefetch"
        type="text/css"
        href={config.commonFontCssUrl}
        media="screen"
        as="style" />

      <link
        rel="canonical"
        href={canonical}
      />

      <script type="text/javascript">{`
        if (typeof window !== 'undefined'){
          var iiAna=window.iiAna=window.iiAna||function iiAnaShim(){(iiAna.q=iiAna.q||[]).push(arguments);}
        }
      `}
      </script>
      <script
        async
        type="text/javascript"
        src={config.analyticsServiceUrl} />
      { /* NOTE: the Amplitude a/b testing script must be SYNChronous, otherwise page may flicker - see https://amplitude.com/docs/web-experiment/implementation */ }
      <script
        type="text/javascript"
        src={config.abTestingScriptSrcUrl} />
      <script
        async
        type="text/javascript"
        src= {`${trimTrailingSlash(config.iiHomeUrl)}/consentmanagement/dist/overrides.min.js`}    />
      <Schema
        schema={rawSchema}
        isFrontPage={isFrontPage}
        featuredImage={seo?.opengraphImage?.sourceUrl}
        skipParsingSchema={skipParsingSchema ?? false} />
    </>
  );
};

